import { Box, Icon, IconId, StaticStep, TimelineContainer, Typography } from '@drone-js/core';
import { Category, SuggestionResult } from 'core/type/SuggestionResult.types';
import { CategoryCms, CategoryId } from 'core/type/Category.types';
import { getTypeDetail, getUpdatedTypeDetail } from 'core/utils/getTextSearchLocation';

import { CmsValue } from 'core/type/BootstrapConfig.types';
import React from 'react';
import { StringFormatter } from '@agoda/react-handyman';
import SuggestionItem from 'component/common/SuggestionItem/SuggestionItem';
import { MapTextSearchTypeIdToText, TextSearchType } from 'core/type/TextSearch.types';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

interface Props {
    result: SuggestionResult;
    onSelect: (result: SuggestionResult, category?: Category) => void;
    isSearchBoxFeatureGroup: boolean;
    searchText?: string;
}

interface TextSearchItemWithCategoriesProps extends Props {
    cms: CmsValue;
}

interface CategoriesTimelineProps {
    cms: CmsValue;
    onSelect: (result: SuggestionResult, category?: Category) => void;
    result: SuggestionResult;
}

interface TextSearchItemProps {
    iconId: IconId;
    title: string;
    titleDetail: string;
    onSelect: () => void;
    isSearchBoxFeatureGroup: boolean;
    searchText?: string;
    searchType?: TextSearchType;
}

const TextSearchIcons: Record<TextSearchType, IconId | undefined> = {
    [TextSearchType.Activity]: 'fill.activity.ferris-wheel',
    [TextSearchType.City]: 'fill.symbol.pin',
    [TextSearchType.CategoryInCity]: undefined,
    [TextSearchType.Unknown]: undefined,
};

const StyledTimelineContainer = styled(TimelineContainer)`
    li {
        div:nth-child(3) {
            width: 100%;
            padding-bottom: 0;
        }
        > div:nth-child(2) {
            > div:first-child {
                display: none;
            }
        }
    }
`;

export const TextSearchItem: React.FC<TextSearchItemProps> = React.memo((props) => {
    return props.isSearchBoxFeatureGroup ? (
        <SuggestionItem
            searchText={props.searchText}
            onSelect={props.onSelect}
            title={props.title}
            subTitle={props.titleDetail}
            icon={props.iconId}
            elementName="activities-overlay-text-search-result-item"
            searchType={props.searchType}
        />
    ) : (
        <Box
            as="button"
            px="8"
            py="12"
            borderRadius="8"
            display="flex"
            onClick={props.onSelect}
            data-testid="activities-overlay-text-search-result-item"
            data-element-name="activities-overlay-text-search-result-item"
            aria-label={props.title}
            interactive="product.primary"
            textAlign="start"
            width="100%"
        >
            {props.iconId && <Icon id={props.iconId} size="24" color="product.primary" me="12" />}
            <div>
                <Typography variant="body-default-strong">{props.title}</Typography>
                <Typography variant="caption-default" textColor="base.subtle">
                    {props.titleDetail}
                </Typography>
            </div>
        </Box>
    );
});
TextSearchItem.displayName = 'TextSearchItem';

const TextSearchResultItem: React.FC<Props> = React.memo((props) => {
    const { result, isSearchBoxFeatureGroup } = props;
    const cms = useSelector(selectCmsFromBootstrap);
    const isAutoApplyCategoryFilter = useSelector(selectFeatureBehaviorFromBootstrap).AutoApplyCategoryFilter;
    const titleDetail = React.useMemo(() => (isSearchBoxFeatureGroup ? getUpdatedTypeDetail(result, cms) : getTypeDetail(result, cms)), [isSearchBoxFeatureGroup, result, cms]);
    const title = result.name || result.nameTranslated;
    const iconId = TextSearchIcons[result.type] || ('fill.activity.ferris-wheel' as IconId);

    const clickHandler = () => {
        const isAutoApplyCategoryFilterEnabled = isAutoApplyCategoryFilter && result.type === TextSearchType.Activity;
        isAutoApplyCategoryFilterEnabled ? props.onSelect(result, result.categories?.[0]) : props.onSelect(result);
    };

    return (
        <TextSearchItem
            searchText={props.searchText}
            iconId={iconId}
            title={title}
            titleDetail={titleDetail}
            onSelect={clickHandler}
            isSearchBoxFeatureGroup={isSearchBoxFeatureGroup}
            searchType={result.type}
        />
    );
});
TextSearchResultItem.displayName = 'TextSearchResultItem';

export default TextSearchResultItem;

export const CategoriesTimeline: React.FC<CategoriesTimelineProps> = React.memo((props) => {
    const { topCategories, cityName, cityNameTranslated } = props.result;
    const city = cityName || cityNameTranslated;
    return (
        <Box me="12" ms="24" my="4">
            <StyledTimelineContainer direction="vertical">
                <StaticStep
                    intent="primary"
                    hierarchy="tertiary"
                    data-testid="activities-overlay-category-in-city"
                    data-element-name="activities-overlay-category-in-city"
                    size="s"
                    endSlot={
                        <Box display="flex" gap="4" ms="16" flexDirection="column" width="100%" alignItems="flex-start">
                            {topCategories?.map((category, index) => {
                                type CategoryIdType = keyof typeof CategoryId;
                                const categoryValue = props.cms[CategoryCms[category.code as CategoryIdType]];
                                return (
                                    <Box
                                        data-testid="activities-overlay-category-in-city-item"
                                        data-element-name="activities-overlay-category-in-city-item"
                                        data-element-index={index}
                                        data-element-value={categoryValue}
                                        data-result-type={MapTextSearchTypeIdToText[TextSearchType.CategoryInCity]}
                                        key={categoryValue}
                                        onClick={() => props.onSelect(props.result, category)}
                                        borderRadius="4"
                                        pb="8"
                                        pt="8"
                                        me="24"
                                        interactive="product.primary"
                                        as="button"
                                        width="100%"
                                        gap="12"
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="center"
                                    >
                                        <Icon id="fill.symbol.search" size="16" />
                                        <Box gap="4" display="flex" flexDirection="row" alignItems="center">
                                            {StringFormatter.formatToJsx(
                                                props.cms[415832],
                                                <Typography variant="body-s-strong">{categoryValue}</Typography>,
                                                <Typography variant="body-s" textColor="base.subtle">
                                                    {city}
                                                </Typography>,
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    }
                />
            </StyledTimelineContainer>
        </Box>
    );
});
CategoriesTimeline.displayName = 'CategoriesTimeline';

export const TextSearchItemWithCategories: React.FC<TextSearchItemWithCategoriesProps> = React.memo((props) => {
    return (
        <>
            <TextSearchResultItem {...props} />
            <CategoriesTimeline onSelect={props.onSelect} cms={props.cms} result={props.result} />
        </>
    );
});
TextSearchItemWithCategories.displayName = 'TextSearchItemWithCategories';
